import * as sentinelCookieUtility from '@sparta.homepage.utilities/sentinel-cookie-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import benchmarkUtility from '@sparta.sales.utilities/benchmark-utility/dist/es6.js';

/* eslint-disable no-undef */

/**
 * @name        Homepage Bootstrap Utility
 * @file        homepage-bootstrap-utility.js
 * @author      Jeremy Cox
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 * @global
 */

const COOKIE_DOMAIN = spaParams.siteParams.env === 'local' ? '' : 'domain=.bankofamerica.com';
const SIGN_IN_FORM_ID = 'signinClient';
const COOKIE_NAME_SB_IGNORE_REDIRECT = 'ignoreSBRedirect';
const COOKIE_EXPIRE_SB_IGNORE_REDIRECT = 1 * 6e4; // 1 minute
const NAV_PERSONAL_ID = 'NAV_PERSONAL';
let initialized = false;
let impressionCalls = 0;

/**
 * @class HomepageBootstrapUtility
 */
class HomepageBootstrapUtility {
  /**
   * constructor
   */
  constructor() {
    this.isSignOff = window.location.search.includes('page_msg=signoff');
    spartaStoreUtility.addData('isSignoff', this.isSignOff);

    // for the new business homepage (www.bankofamerica.com/business)
    this.isBusHP = spaParams.siteParams.env === 'local' ? window.location.pathname.startsWith('/homepage/business') : window.location.pathname.startsWith('/business');
    this.isSBPHP = window.location.search.includes('SBPHP');
    this.isSBCHP = window.location.search.includes('SBCHP');
    this.isMSBHP = window.location.search.includes('MSBHP');
    spartaStoreUtility.addData('isSBPHP', this.isSBPHP);
    spartaStoreUtility.addData('isSBCHP', this.isSBCHP);
    spartaStoreUtility.addData('isMSBHP', this.isMSBHP);
    if (spaParams.siteParams.jsTimer) {
      benchmarkUtility.init(this);
    }
  }

  /**
   * Initialize module.
   *
   * @mockSpaParams {
   *   siteParams: {
   *     impressionThreshold: 2,
   *     targetingEndpoint: '',
   *    }
   * }
   * @testExample init()
   * @testOutput equal undefined
   * @testExpect window.fireImpressions toBeA 'function'
   * @returns {void}
   */
  init() {
    // only initialize once
    if (initialized) return;
    initialized = true;
    this.setLangCookies();
    this.setIgnoreSbCookie();
    this.bindEventListeners();
    this.expires();
    document.addEventListener('sitePromotionsProcessed', () => {
      if (typeof bactm !== 'undefined') {
        if (typeof bactm.pageview === 'function') {
          let pageviewIndex = 0;
          if (this.isSignOff) pageviewIndex = 1;

          // Only check if on business page.
          if (this.isBusHP) {
            if (this.isSBPHP) pageviewIndex = 2;
            if (this.isSBCHP) pageviewIndex = 3;
            if (this.isMSBHP) pageviewIndex = 4;
          }
          bactm.pageview(pageviewIndex);
        }
      }
    });
    window.fireImpressions = function windowFireImpressions(ignoreThreshold = false) {
      impressionCalls += 1;
      const thresholdMet = impressionCalls >= spaParams.siteParams.impressionThreshold;
      if (typeof bactm !== 'undefined') {
        if (typeof bactm.impressions === 'function' && (thresholdMet || ignoreThreshold)) {
          bactm.impressions();
        }
      }
    };

    // Log browser info
    const browser = spartaStoreUtility.getData('browser');
    const {
      name,
      version: v
    } = browser;
    let device;
    if (browser.desktop) {
      device = 'desktop';
    } else if (browser.mobile) {
      device = 'mobile';
    } else if (browser.tablet) {
      device = 'tablet';
    }
    if (typeof bactm !== 'undefined' && typeof bactm.log !== 'undefined') {
      bactm.log({
        type: 'device',
        name,
        v,
        device
      });
    }
    sentinelCookieUtility.init();
  }

  /**
   * Bind event listeners.
   *
   * @mockCookies
   * @mockElement form#signinClient
   * @mockElement a#NAV_PERSONAL
   * @testExample bindEventListeners()
   * @testOutput equal undefined
   * @testTrigger click mockedElements[1]
   * @testExpect document.cookie toInclude 'ignoreSBRedirect'
   * @testTrigger submit mockedElements[0]
   * @testExpect document.cookie notToInclude 'ignoreSBRedirect'
   * @return {void}
   */
  bindEventListeners() {
    this.delegateEvent('submit', `#${SIGN_IN_FORM_ID}`, this.handleSignInSubmit.bind(this));
    const navPersonal = document.getElementById(NAV_PERSONAL_ID);
    if (navPersonal) {
      navPersonal.addEventListener('click', this.setIgnoreSbCookie.bind(this));
    }
  }

  /**
   * handleSignInSubmit event handler for sign in form submit
   *
   * @return {void}
   */
  handleSignInSubmit() {
    this.deleteCookie(COOKIE_NAME_SB_IGNORE_REDIRECT);
  }

  /**
   * Delegate event listeners to the module container so dom re-rendering will not double bind etc.
   *
   * @param {String} event - event to listen for
   * @param {String} selector - selector to delegate to target
   * @param {Function} handler - handler for event being listened for
   * @returns {void}
   */
  delegateEvent(event, selector, handler) {
    document.addEventListener(event, function delegateTarget(e) {
      for (let t = e.target; t && t !== this; t = t.parentNode) {
        if (t.matches(selector)) {
          handler.call(t, e);
          break;
        }
      }
    }, false);
  }

  /**
   * setLangCookies
   *
   * @return {void}
   */
  setLangCookies() {
    const langAttr = document.getElementsByTagName('html')[0].getAttribute('lang');
    const lang = langAttr.replace('-', '_') || 'en_US';
    this.setCookie('CONTEXT', lang);
    this.setCookie('INTL_LANG', lang);
    this.setCookie('LANG_COOKIE', lang);
    this.setCookie('BOFA_LOCALE_COOKIE', langAttr);
  }

  /**
   * setIgnoreSbCookie
   *
   * @return {void}
   */
  setIgnoreSbCookie() {
    const expires = new Date(+new Date() + COOKIE_EXPIRE_SB_IGNORE_REDIRECT);
    this.setCookie(COOKIE_NAME_SB_IGNORE_REDIRECT, 'true', expires.toUTCString());
  }

  /**
   * setCookie by name and value
   *
   * @param {String} name cookie name
   * @param {String} value cookie value
   * @param {String} expires expiration
   * @returns {void}
   */
  setCookie(name, value, expires) {
    const expiresSegment = expires ? `expires=${expires};` : 'true';
    document.cookie = `${name}=${value}; ${COOKIE_DOMAIN}; path=/; ${expiresSegment}`;
  }

  /**
   * deleteCookie by cookie name
   *
   * @param {String} name cookie name
   * @returns {void}
   */
  deleteCookie(name) {
    document.cookie = `${name}=; ${COOKIE_DOMAIN}; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }

  /**
   * getUrlParameter by param name
   *
   * @param {String} name parameter name
   * @returns {void}
   */
  getUrlParameter(name) {
    const replacedName = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${replacedName}=([^&#]*)`);
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }

  /**
   * If site param enableExpirationBounce is true,
   * it checks if the bactoken is expired or doesn't exist and redirects to the homepage
   *
   * @returns {void}
   */
  expires() {
    if (!spaParams.siteParams.enableExpirationBounce) return;
    const bactoken = parseInt(this.getUrlParameter('bactoken'), 10) || 0;
    const nowFromToken = bactoken / 73;
    const now = Date.parse(new Date()) / 1000;
    const expired = nowFromToken < now - 1200;
    const url = `${spaParams.siteParams.absoluteLink}` || 'https://www.bankofamerica.com';
    const path = `${spaParams.pageParams.expirationRedirect}` || '/';
    let redirectTo = url + path;
    if (sparta.language !== 'en') {
      redirectTo += `${sparta.language}/`;
    }
    if (expired) {
      window.location.replace(redirectTo);
    }
  }
}

// Export the homepageBootstrapUtility for use in modules and utilities
var index = new HomepageBootstrapUtility();

// Mock undefined site params for Adobe code when running in unit test environment.
if (window.mocha && window.chai) {
  window.spaParams = {
    siteParams: {
      impressionThreshold: 2,
      targetingEndpoint: ''
    }
  };
}

/**
 * @fileoverview Stub out the bactm.pageview function, making it accessible before utag.js loads.
 *
 * @requires Tag Manager Module
 *
 * utag.js is the core Tag Manager library. Stubbed out function below has no functionality
 * without properly loading the tag manager module and should only be used in instances
 * where module may need to access function before utag.js has had the opportunity to load.
 *
 * @author Vendor Technology Integration & Management (dg.vtim@bankofamerica.com)
 */

/**
 * Captures data from digitalData object to call a Coremetrics pageview and adds to the
 * dataCollector queue for processing.
 *
 * @public
 * @param {string|number} [pageInfoKey] - Value indicating how to find the pageInfo
 *      from the digitalData object. Can be the index of the array, pageID, or the
 *      associated value.
 * @param {object} [options] - Object of options for this pageview call. Valid
 *      properties include segmentValues {array}, categoryType {string}, lookupType {string},
 *      isSaveAndReturn {bool}, saveAndReturnId {string}, applicationId {string}
 *      cartProducts {array} and isApp {bool}.
 * @returns {void}
 */
/* eslint-disable */
(function () {
  "undefined" == typeof window.bactm && (window.bactm = {}, window.bactm.pageview = function (o, n) {
    window.dataCollector = window.dataCollector || [], window.dataCollector.push({
      event: "pageview",
      pageInfoKey: o,
      options: n
    });
  }, window.bactm.log = function (t) {
    window.dataCollector = window.dataCollector || [];
    Array.isArray(t) || "object" != typeof t || "ts" in t || (t.ts = function () {
      var t = new Date();
      return t.getTime() - t.getTimezoneOffset();
    }()), window.dataCollector.push({
      event: "log",
      data: t
    });
  }, window.bactm.impressions = function () {
    window.bactm_fireImpressions = true;
  });
})();

export { index as default };
